import styled from 'styled-components'

import { media } from 'featureSliced/shared/utils/breakpoints'

const CHROME = '#434343'

export const WebScreen = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  background: ${({ theme }) => theme.MainBackGround};
`

/* ---------- header: logo + search left, actions right, burger last ---------- */

export const HEADER_HEIGHT = 70

export const WebHeaderBar = styled.header`
  display: flex;
  align-items: center;
  gap: 20px;
  height: ${HEADER_HEIGHT}px;
  padding: 0 24px;
  background: ${CHROME};
  /* stays put while the middle column scrolls; below the sidebar scrim (5) and
     the modal overlay (5) so both still cover it */
  position: sticky;
  top: 0;
  z-index: 3;

  ${media.wide} {
    padding: 0 32px;
  }
`

export const WebLogoButton = styled.button`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
`

export const WebLogo = styled.img`
  height: 30px;
  width: auto;
  object-fit: contain;
  flex-shrink: 0;
`

export const WebSearch = styled.div`
  flex-grow: 1;
  min-width: 0;
  max-width: 420px;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 0 16px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.12);
`

export const WebSearchInput = styled.input`
  flex-grow: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: #fff;
  font-size: 14px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.72);
  }
`

export const WebHeaderSpacer = styled.div`
  flex-grow: 1;
`

export const WebActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`

export const WebAction = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  min-width: 44px;
  padding: 0 14px;
  border-radius: 5px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: transparent;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`

export const WebActionLabel = styled.span`
  display: none;

  ${media.wide} {
    display: inline;
  }
`

export const WebCartAction = styled(WebAction)`
  border-color: transparent;
  background: ${({ theme }) => theme.colour};
`

/* ---------- three columns ---------- */

export const WebBody = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 24px;
  width: 100%;

  ${media.wide} {
    gap: 24px;
    padding: 32px;
  }
`

export const WebSide = styled.aside`
  width: 240px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
  position: sticky;
  /* clears the sticky header, otherwise the categories slide under it */
  top: ${HEADER_HEIGHT + 24}px;
  /* a sticky column taller than the viewport can never be scrolled by the page —
     cap it and let it scroll itself, or long category lists are unreachable */
  max-height: calc(100vh - ${HEADER_HEIGHT + 24 + 24}px);
  overflow-y: auto;
  overscroll-behavior: contain;

  ${media.wide} {
    width: 280px;
  }
`

export const WebMain = styled.main`
  flex-grow: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;

  /* the product grid is shared with the phone layout — only widen it here.
     160px min keeps every tablet tile under the desktop's 253px: the widest a
     track can get before a third/fourth column fits is 249px. */
  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    grid-gap: 20px;
  }

  ${media.wide} {
    .products-grid {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }
`

export const WebCartColumn = styled.aside`
  display: none;

  ${media.wide} {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 340px;
    flex-shrink: 0;
    position: sticky;
    top: ${HEADER_HEIGHT + 32}px;
    padding: 20px;
    border-radius: 10px;
    border: 0.5px solid ${({ theme }) => theme.ItemsBorders};
    background: ${({ theme }) => theme.ItemsBackGround};
  }
`

/* ---------- cards inside the side / cart columns ---------- */

export const WebCard = styled.div`
  padding: 16px;
  border-radius: 10px;
  border: 0.5px solid ${({ theme }) => theme.ItemsBorders};
  background: ${({ theme }) => theme.ItemsBackGround};
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const WebCardLabel = styled.p`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  line-height: 14px;
  opacity: 0.72;
  color: ${({ theme }) => theme.FontColor};
`

export const WebCardValue = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: ${({ theme }) => theme.FontColor};
`

export const WebCardRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`

/* QR build: caption and number read as one phrase, and the whole card opens the
   table picker — the same modal the phone layout prompts with. */
export const WebTableCard = styled.button`
  padding: 16px;
  border-radius: 10px;
  border: 0.5px solid ${({ theme }) => theme.ItemsBorders};
  background: ${({ theme }) => theme.ItemsBackGround};
  display: flex;
  align-items: baseline;
  gap: 6px;
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: ${({ theme }) => theme.colour};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colour};
    outline-offset: 2px;
  }
`

export const WebDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.ItemsBorders};
`

export const WebLink = styled.button`
  align-self: flex-start;
  display: flex;
  align-items: center;
  height: 44px;
  padding: 0;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colour};
  cursor: pointer;
`

export const WebGroupTitle = styled.p`
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  opacity: 0.72;
  color: ${({ theme }) => theme.FontColor};
`

export const WebCategoryList = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const WebCategoryButton = styled('button')<{ isActive?: boolean }>`
  height: 46px;
  padding: 0 14px;
  border: none;
  border-left: 3px solid
    ${({ isActive, theme }) => (isActive ? theme.colour : 'transparent')};
  border-radius: 5px;
  background: ${({ isActive, theme }) =>
    isActive ? theme.ItemsBackGround : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 15px;
  font-weight: ${({ isActive }) => (isActive ? 600 : 500)};
  color: ${({ theme }) => theme.FontColor};
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.ItemsBackGround};
  }
`

export const WebSectionTitle = styled.h2`
  margin: 0 0 16px;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  color: ${({ theme }) => theme.FontColor};
`

/* ---------- cart panel ---------- */

export const WebCartTitle = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
`

export const WebCartLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

export const WebCartThumb = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 5px;
  object-fit: contain;
  flex-shrink: 0;
`

export const WebCartLineBody = styled.div`
  flex-grow: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

/* row 2 of a cart line: "qty × unit" on the left, the line total on the right */
export const WebCartLineMeta = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`

/* row 3 of a cart line: stepper at the start (when the product has one), edit/remove at the end */
export const WebCartLineControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

export const WebStepper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const WebStepButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 5px;
  border: 1px solid ${({ theme }) => theme.ItemsBorders};
  background: transparent;
  color: ${({ theme }) => theme.FontColor};
  font-size: 18px;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`

/* carries the white <img> glyphs (basket/trashCan) — they need a dark ground
   on both themes (QRM-647 rule) */
export const WebIconButton = styled(WebStepButton)`
  border: none;
  background: rgba(0, 0, 0, 0.55);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.22);
  display: inline-flex;
  align-items: center;
  justify-content: center;

  img {
    width: 22px;
    height: 22px;
  }
`

/* remove is the destructive action — same red as the allergen marks (QRM-647) */
export const WebRemoveButton = styled(WebIconButton)`
  background: #ff5151;
  box-shadow: none;

  img {
    width: 24px;
    height: 24px;
  }
`

export const WebLineActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`

export const WebCartEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 34px 8px 40px;
  text-align: center;
  color: ${({ theme }) => theme.FontColor};
`

export const WebTotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.FontColor};
`

export const WebTotalValue = styled.span`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colour};
`

/* ---------- checkout (QRM-641 S4) ---------- */

export const WebCheckoutBody = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 28px 24px 64px;

  ${media.wide} {
    padding: 28px 32px 64px;
  }
`

export const WebCheckoutInner = styled.div`
  width: 100%;
  max-width: 1052px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const WebBackLink = styled.button`
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.FontColor};
  opacity: 0.75;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
`

export const WebCheckoutTitle = styled.h1`
  margin: 0 0 8px;
  font-size: 26px;
  font-weight: 600;
  line-height: 32px;
  color: ${({ theme }) => theme.FontColor};
`

export const WebCheckoutForm = styled('div')<{ withSummary?: boolean }>`
  display: grid;
  gap: 24px;
  align-items: start;
  grid-template-columns: minmax(0, 1fr);

  ${media.wide} {
    grid-template-columns: ${({ withSummary }) =>
      withSummary ? 'minmax(0, 1fr) 380px' : 'minmax(0, 1fr)'};
    gap: 32px;
  }
`

export const WebSummaryPanel = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
  border-radius: 10px;
  border: 0.5px solid ${({ theme }) => theme.ItemsBorders};
  background: ${({ theme }) => theme.ItemsBackGround};

  ${media.wide} {
    position: sticky;
    top: ${HEADER_HEIGHT + 32}px;
  }
`

export const WebTextButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  color: ${({ theme }) => theme.colour};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

export const WebLineComponentList = styled.ul`
  margin: 0;
  padding: 0 0 0 10px;
  list-style: none;
  font-size: 12px;
  line-height: 16px;
  opacity: 0.72;
  color: ${({ theme }) => theme.FontColor};
`
